/**
 * The EHR error registry (ADR-251): one logical code per failure MEANING, shared by
 * every connector, so an expired password is `AUTH_INVALID_CREDENTIALS` whether it
 * came from athena, Office Ally or the next EHR.
 *
 * Each connector translates its EHR's own failures into these codes at the source.
 * The EHR's original wording travels separately as `detail` (for logs and support),
 * never as the message: `message` is ours, the same for every EHR, and written for
 * the practice staff who see it. `description` is written for the voice agent's
 * model — what the failure means for the caller in front of it.
 *
 * Grow this as new failures are found: add a code here, then map to it in the
 * connector that raises it. Never reuse a code for a different meaning.
 */

export const EHR_ERROR_CATEGORIES = [
  /** Our credentials or permissions are wrong. A person has to fix them. */
  'auth',
  /** The EHR (or the way to it) is down, slow or throttling. Retrying helps. */
  'availability',
  /** The EHR refused the request as sent. Retrying the same request won't help. */
  'request',
  /** The thing asked about doesn't exist in the EHR. */
  'not_found',
  /** Someone else got there first (a slot was taken). */
  'conflict',
  /** The connector can't do this, or isn't set up to. */
  'capability',
  /** The EHR answered but we couldn't read what it sent. */
  'data',
  /** A failure inside the connector itself. */
  'internal',
] as const;

export type EhrErrorCategory = (typeof EHR_ERROR_CATEGORIES)[number];

export interface EhrErrorDefinition {
  category: EhrErrorCategory;
  /** Whether retrying the same request later can succeed. */
  retryable: boolean;
  /** A person must act (fix a credential, grant access) before this can succeed. */
  needsHumanAction: boolean;
  /** Standard, EHR-neutral message for practice staff. */
  message: string;
  /** For the voice agent's model: what this means for the caller. */
  description: string;
}

export const EHR_ERRORS = {
  // ── auth ─────────────────────────────────────────────────────────────────────
  AUTH_INVALID_CREDENTIALS: {
    category: 'auth',
    retryable: false,
    needsHumanAction: true,
    message: 'The EHR rejected our credentials. Reconnect the integration to fix this.',
    description:
      "The practice's system couldn't be reached with our current access. Nothing can be read or changed there right now; staff need to follow up.",
  },
  AUTH_PASSWORD_RESET_REQUIRED: {
    category: 'auth',
    retryable: false,
    needsHumanAction: true,
    message:
      "The EHR requires a password change for the integration's account. Change it in the EHR, then reconnect.",
    description:
      "The practice's system couldn't be reached with our current access. Nothing can be read or changed there right now; staff need to follow up.",
  },
  AUTH_PERMISSION_DENIED: {
    category: 'auth',
    retryable: false,
    needsHumanAction: true,
    message:
      "The EHR says the integration isn't allowed to do this. Check the integration's permissions in the EHR.",
    description:
      "The practice's system didn't allow this action. It can't be done from here; staff need to follow up.",
  },
  EHR_SECURITY_CHALLENGE: {
    category: 'auth',
    retryable: false,
    needsHumanAction: true,
    message:
      'The EHR asked for an extra security check (such as a CAPTCHA or verification code) that the integration cannot complete.',
    description:
      "The practice's system couldn't be reached with our current access. Staff need to follow up.",
  },
  AUTH_SESSION_EXPIRED: {
    category: 'auth',
    retryable: true,
    needsHumanAction: false,
    message: 'The EHR session expired. It will be renewed automatically.',
    description: "The practice's system needed a moment to reconnect. Trying again may work.",
  },

  // ── availability ─────────────────────────────────────────────────────────────
  EHR_UNAVAILABLE: {
    category: 'availability',
    retryable: true,
    needsHumanAction: false,
    message: 'The EHR is temporarily unavailable. We will retry.',
    description:
      "The practice's system is temporarily unavailable. The request didn't go through yet.",
  },
  EHR_UNREACHABLE: {
    category: 'availability',
    retryable: true,
    needsHumanAction: false,
    message: "We couldn't reach the EHR connector. We will retry.",
    description:
      "The practice's system couldn't be reached just now. The request didn't go through yet.",
  },
  EHR_TIMEOUT: {
    category: 'availability',
    retryable: true,
    needsHumanAction: false,
    message: 'The EHR took too long to respond. We will retry.',
    description:
      "The practice's system took too long to answer. It's not known whether the request went through.",
  },
  EHR_RATE_LIMITED: {
    category: 'availability',
    retryable: true,
    needsHumanAction: false,
    message: 'The EHR is limiting how fast we can send requests. We will retry shortly.',
    description: "The practice's system is busy. The request didn't go through yet.",
  },

  // ── request ──────────────────────────────────────────────────────────────────
  EHR_REQUEST_REJECTED: {
    category: 'request',
    retryable: false,
    needsHumanAction: false,
    message: 'The EHR rejected the request.',
    description:
      "The practice's system refused this request as made. Trying the exact same thing again won't help.",
  },
  EHR_MISSING_REQUIRED_FIELDS: {
    category: 'request',
    retryable: false,
    needsHumanAction: false,
    message: 'The EHR needs more information to complete this.',
    description:
      "The practice's system needs more details before this can be done (for example an address or phone number).",
  },
  SEARCH_CRITERIA_INSUFFICIENT: {
    category: 'request',
    retryable: false,
    needsHumanAction: false,
    message: 'Not enough details to search the EHR.',
    description: 'More details are needed to look this person up.',
  },
  INVALID_REQUEST: {
    category: 'request',
    retryable: false,
    needsHumanAction: false,
    message: 'The request to the EHR connector was invalid.',
    description: "Something went wrong on our side preparing this request. It wasn't sent.",
  },

  // ── not found ────────────────────────────────────────────────────────────────
  PATIENT_NOT_FOUND: {
    category: 'not_found',
    retryable: false,
    needsHumanAction: false,
    message: "The patient wasn't found in the EHR.",
    description: "No matching patient record was found in the practice's system.",
  },
  APPOINTMENT_NOT_FOUND: {
    category: 'not_found',
    retryable: false,
    needsHumanAction: false,
    message: "The appointment wasn't found in the EHR.",
    description: "That appointment wasn't found in the practice's system.",
  },
  RESOURCE_NOT_FOUND: {
    category: 'not_found',
    retryable: false,
    needsHumanAction: false,
    message: "The requested record wasn't found in the EHR.",
    description: "That record wasn't found in the practice's system.",
  },

  // ── conflict ─────────────────────────────────────────────────────────────────
  SLOT_UNAVAILABLE: {
    category: 'conflict',
    retryable: false,
    needsHumanAction: false,
    message: 'That time is no longer available.',
    description: 'That time was just taken. Offer a different time.',
  },

  /**
   * The slot is gone from the EHR's schedule (deleted, or removed by a template
   * change), rather than booked by someone. Same handling as SLOT_UNAVAILABLE for
   * a live booking; kept apart so a replay of our own pinned write can tell that
   * it never landed (ADR-273).
   */
  SLOT_REMOVED: {
    category: 'conflict',
    retryable: false,
    needsHumanAction: false,
    message: 'That time is no longer available.',
    description: 'That time is no longer on the schedule. Offer a different time.',
  },

  EHR_CONFLICT: {
    category: 'conflict',
    retryable: false,
    needsHumanAction: false,
    message: 'The EHR reported a conflict with its current records.',
    description:
      "The practice's system says this clashes with something already there. Trying the exact same thing again won't help.",
  },

  // ── capability ───────────────────────────────────────────────────────────────
  NOT_IMPLEMENTED: {
    category: 'capability',
    retryable: false,
    needsHumanAction: false,
    message: "This EHR integration doesn't support this action yet.",
    description: "This can't be done in the practice's system from here; staff need to follow up.",
  },
  NOT_CONFIGURED: {
    category: 'capability',
    retryable: false,
    needsHumanAction: true,
    message: "The EHR integration isn't fully set up for this.",
    description: "This can't be done from here right now; staff need to follow up.",
  },

  // ── data ─────────────────────────────────────────────────────────────────────
  SYNC_ITEM_FAILED: {
    category: 'data',
    retryable: true,
    needsHumanAction: false,
    message: "One record from the EHR couldn't be read and was skipped.",
    description: "Part of the practice's records couldn't be read.",
  },
  SCRAPER_SELECTOR_FAILED: {
    category: 'data',
    retryable: false,
    needsHumanAction: false,
    message: "The EHR's pages changed and the integration couldn't read them.",
    description: "The practice's system couldn't be read just now; staff need to follow up.",
  },

  // ── internal ─────────────────────────────────────────────────────────────────
  SYNC_RUN_STALLED: {
    category: 'internal',
    retryable: true,
    needsHumanAction: false,
    message:
      'The sync stopped making progress and was stopped. The next scheduled sync will try again.',
    description: "The practice's records are being refreshed and may be slightly out of date.",
  },
  CONNECTOR_INTERNAL_ERROR: {
    category: 'internal',
    retryable: true,
    needsHumanAction: false,
    message: 'The EHR integration hit an unexpected error.',
    description: 'Something went wrong on our side. The request may not have gone through.',
  },
  EHR_UNKNOWN_ERROR: {
    category: 'internal',
    retryable: true,
    needsHumanAction: false,
    message: 'The EHR returned an unexpected error.',
    description:
      "The practice's system returned an unexpected error. It's not known whether the request went through.",
  },
} as const satisfies Record<string, EhrErrorDefinition>;

export type EhrErrorCode = keyof typeof EHR_ERRORS;

/** Every registered code, as `{ CODE: 'CODE' }`, for call sites that name a code. */
export const EHR_ERROR_CODES = Object.fromEntries(
  Object.keys(EHR_ERRORS).map((code) => [code, code])
) as { readonly [K in EhrErrorCode]: K };

export function isEhrErrorCode(code: string | undefined | null): code is EhrErrorCode {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(EHR_ERRORS, code);
}

/**
 * The definition for a code, with the code itself. A code that isn't registered
 * (an older connector, a typo, a future EHR not yet mapped) resolves to
 * `EHR_UNKNOWN_ERROR`, so callers always get a definition to act on.
 */
export function ehrErrorDefinition(
  code: string | undefined | null
): EhrErrorDefinition & { code: EhrErrorCode } {
  const known: EhrErrorCode = isEhrErrorCode(code) ? code : 'EHR_UNKNOWN_ERROR';
  return { code: known, ...EHR_ERRORS[known] };
}

/**
 * The HTTP status a connector answers with for a code. Callers key on the code,
 * not the status, but the status still has to be honest: 409 for a taken slot,
 * 401/403 for auth, 5xx only when the fault isn't the request.
 */
export function ehrErrorHttpStatus(code: string | undefined | null): number {
  const def = ehrErrorDefinition(code);
  switch (def.code) {
    case 'AUTH_PERMISSION_DENIED':
    case 'EHR_SECURITY_CHALLENGE':
      return 403;
    case 'EHR_RATE_LIMITED':
      return 429;
    case 'EHR_TIMEOUT':
      return 504;
    case 'EHR_REQUEST_REJECTED':
    case 'EHR_MISSING_REQUIRED_FIELDS':
      return 422;
    case 'NOT_IMPLEMENTED':
      return 501;
    case 'NOT_CONFIGURED':
      return 503;
    case 'EHR_UNKNOWN_ERROR':
      // The EHR's answer was unexpected: an upstream fault, not ours.
      return 502;
    default:
      break;
  }
  switch (def.category) {
    case 'auth':
      return 401;
    case 'request':
      return 400;
    case 'not_found':
      return 404;
    case 'conflict':
      return 409;
    case 'internal':
      return 500;
    default:
      return 502;
  }
}

/**
 * Split a failure recorded as `CODE: message` (the form connectors and the API
 * wrote before codes had their own field) into its code and message. Only a
 * registered code is split off; any other text comes back whole, with no code.
 */
export function splitCodedMessage(text: string): { code: EhrErrorCode | null; message: string } {
  const sep = text.indexOf(': ');
  if (sep > 0) {
    const head = text.slice(0, sep);
    if (isEhrErrorCode(head)) return { code: head, message: text.slice(sep + 2) };
  }
  return { code: null, message: text };
}

/**
 * The one-line `CODE: message` form for operator-facing text (alert emails,
 * logs), where the code and message are stored apart. Without a code the text
 * comes back as it is; with neither, null.
 */
export function codedErrorText(
  code: string | null | undefined,
  message: string | null | undefined
): string | null {
  if (!message) return code ?? null;
  return code ? `${code}: ${message}` : message;
}
